import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CustomSelect } from '@/components';
import { syncWindexContacts } from '../../api';
import windexIcon from '../../assets/Windex_icon.jpg';
import { windexFilterMessage } from './windexContactFilters';

const currentYear = new Date().getFullYear();

function academicYear(start) {
  const end = Number(start) + 1;
  return `${start}-${String(end).slice(-2)}`;
}

const yearOptions = Array.from({ length: 16 }, (_, index) => {
  const label = academicYear(currentYear - index);
  return { value: label, label };
});

const selectClass = 'h-10 w-full min-w-0';
const buttonClass =
  'h-10 w-full rounded-xl bg-white border border-[#EBF4EA] px-3 text-sm font-medium text-[#1F2241] flex items-center justify-between';

function syncResult(body, errorMessage) {
  if (errorMessage) {
    return {
      total: 0,
      imported: 0,
      failed: 1,
      errors: [{ text: errorMessage }],
    };
  }
  const created = Number(body?.created_count) || 0;
  const updated = Number(body?.updated_count) || 0;
  const contacts = Array.isArray(body?.contacts) ? body.contacts : [];
  const empty = body?.provider_status === 'no_data' && contacts.length === 0;
  const synced = created + updated;
  return {
    total: synced,
    imported: synced,
    failed: 0,
    errors: empty ? [{ text: 'No contacts returned for this year' }] : [],
  };
}

export function WindexYearSync({ companyId, onSynced }) {
  const queryClient = useQueryClient();
  const [year, setYear] = useState(yearOptions[0].value);
  const [result, setResult] = useState(null);

  const syncMutation = useMutation({
    mutationFn: (value) => syncWindexContacts(companyId, value),
    onSuccess: (body, value) => {
      const next = syncResult(body);
      setResult(next);
      if (next.errors.length) return;
      queryClient.invalidateQueries({
        queryKey: ['windex-contact-years', companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-contacts', companyId],
      });
      onSynced?.(body?.year || value);
    },
    onError: (err) => {
      setResult(syncResult(null, windexFilterMessage(err)));
    },
  });

  return (
    <div className="flex shrink-0 flex-col gap-3 rounded-[8px] bg-[#F5F5F5] px-3.5 py-3 sm:px-4 sm:py-3.5">
      <div className="flex min-w-0 items-start gap-2">
        <img
          src={windexIcon}
          alt=""
          className="mt-0.5 h-8 w-10 shrink-0 rounded object-contain"
        />
        <div className="min-w-0">
          <p className="text-[14px] font-semibold leading-[22px] text-[#1F2241] sm:text-[15px]">
            Sync from Windex
          </p>
          <p className="text-[12px] leading-[18px] text-[#868686] sm:text-[13px]">
            Choose a year, then pull those contacts into this list.
          </p>
        </div>
      </div>
      <label className="block min-w-0 text-[13px] font-medium text-[#1F2241]">
        Academic Year
        <CustomSelect
          value={year}
          onChange={(value) => setYear(value || yearOptions[0].value)}
          options={yearOptions}
          className={`${selectClass} mt-1.5`}
          buttonClassName={buttonClass}
          textSizeClass="text-sm font-medium"
        />
      </label>
      <button
        type="button"
        disabled={syncMutation.isPending}
        onClick={() => syncMutation.mutate(year)}
        className="inline-flex h-10 w-full items-center justify-center rounded-[8px] bg-[#50AB3D] px-4 text-[14px] font-medium text-white hover:bg-[#2F8E29] disabled:opacity-70"
      >
        {syncMutation.isPending ? 'Syncing...' : 'Sync Contacts'}
      </button>
      {result
        ? createPortal(
            <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
              <div className="relative flex max-h-[90vh] w-full max-w-lg flex-col rounded-card border border-border-default bg-card-background text-left">
                <div className="flex items-center justify-between border-b border-border-default px-6 py-4">
                  <h3 className="text-h6 font-bold text-text-primary">
                    Import Results
                  </h3>
                  <button
                    type="button"
                    onClick={() => setResult(null)}
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-white hover:opacity-95"
                  >
                    <svg
                      className="h-3 w-3 stroke-[3]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
                <div className="flex flex-col gap-4 overflow-y-auto p-6">
                  <div className="mb-2 grid grid-cols-3 gap-4">
                    <div className="flex flex-col items-center justify-center rounded-xl border border-border-default bg-input-background p-4 text-center">
                      <span className="mb-1 text-2xl font-bold text-text-primary">
                        {result.total}
                      </span>
                      <span className="text-xs text-text-secondary">Total</span>
                    </div>
                    <div className="flex flex-col items-center justify-center rounded-xl border border-success/20 bg-success/10 p-4 text-center">
                      <span className="mb-1 text-2xl font-bold text-success">
                        {result.imported}
                      </span>
                      <span className="text-xs text-success">Imported</span>
                    </div>
                    <div className="flex flex-col items-center justify-center rounded-xl border border-error/20 bg-error/10 p-4 text-center">
                      <span className="mb-1 text-2xl font-bold text-error">
                        {result.failed}
                      </span>
                      <span className="text-xs text-error">Failed</span>
                    </div>
                  </div>
                  {result.errors.length > 0 ? (
                    <div className="flex flex-col gap-2">
                      <h4 className="text-sm font-semibold text-text-primary">
                        Errors
                      </h4>
                      <div className="flex max-h-60 flex-col gap-3 overflow-y-auto rounded-xl border border-border-default bg-input-background p-4">
                        {result.errors.map((error) => (
                          <span
                            key={error.text}
                            className="text-sm font-medium text-error"
                          >
                            {error.text}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="flex justify-end border-t border-border-default p-6">
                  <button
                    type="button"
                    onClick={() => setResult(null)}
                    className="flex h-12 w-[120px] items-center justify-center rounded-xl bg-[#4CAF50] text-sm font-semibold text-white"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
